<template>
  <div
    class="mc-button-wrapper"
    :class="{ 'w-100': block }"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
  >
    <div
      v-if="isSplitButton"
      class="mc-button-split-group"
      :class="{ 'mc-button-split-group--block': block }"
    >
      <b-button
        v-bind="$attrs"
        :variant="computedVariant"
        :class="mainButtonClass"
        :disabled="disabled || loading"
        v-on="$listeners"
        @click="createLog"
      >
        <!-- Todo: add slot for upcoming feature -->
        <b-spinner v-if="loading" small />
        <slot name="before-slot" />
        <i v-if="icon && iconPosition === 'left'" :class="`mc-icon ${icon}`" />
        <span v-if="shouldShowText">{{ text }}</span>
        <i v-if="icon && iconPosition === 'right'" :class="`mc-icon ${icon}`" />
        <slot name="after-slot" />
      </b-button>
      <b-button
        :variant="computedVariant"
        :class="actionButtonClass"
        :disabled="isActionDisabled"
        @click.stop="handleActionClick"
      >
        <slot name="action-slot">
          <i v-if="actionIcon" :class="`mc-icon ${actionIcon}`" />
        </slot>
      </b-button>
    </div>
    <b-button
      v-else
      v-bind="$attrs"
      :variant="computedVariant"
      :class="buttonClass"
      :disabled="disabled || loading"
      v-on="$listeners"
      @click="createLog"
    >
      <!-- Todo: add slot for upcoming feature -->
      <b-spinner v-if="loading" small />
      <slot name="before-slot" />
      <i v-if="icon && iconPosition === 'left'" :class="`mc-icon ${icon}`" />
      <span v-if="shouldShowText">{{ text }}</span>
      <i v-if="icon && iconPosition === 'right'" :class="`mc-icon ${icon}`" />
      <slot name="after-slot" />
    </b-button>
    <div
      v-if="tooltipVisible && (displayTooltip || shouldShowTooltip)"
      class="tooltip"
      :class="{ 'tooltip--nowrap': !tooltipWrap }"
      :data-position="tooltipPosition"
    >
      {{ tooltipText || text }}
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'

export default {
  mixins: [Helper],
  props: {
    text: {
      type: String,
      default: 'Button',
    },
    variant: {
      type: String,
      default: 'primary',
    },
    size: {
      type: String,
    },
    icon: {
      type: String,
    },
    iconPosition: {
      type: String,
      default: 'left',
    },
    disabled: {
      type: Boolean,
    },
    displayTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
    },
    block: {
      type: Boolean,
      default: false,
    },
    borderNone: {
      type: Boolean,
      default: false,
    },
    tooltipPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
    forceShowTooltip: {
      type: Boolean,
      default: false,
    },
    screenBreakpoint: {
      type: Number,
    },
    tooltipWrap: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    split: {
      type: Boolean,
      default: false,
    },
    actionIcon: {
      type: String,
    },
    actionDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tooltipVisible: false,
      isSmallScreen: false,
    }
  },
  computed: {
    baseButtonClasses() {
      const buttonClasses = ['mc-button', `mc-btn__${this.variant}`]

      if (this.borderNone) buttonClasses.push('mc-btn__border-none')
      if (this.size) buttonClasses.push(`mc-btn__${this.size}`)

      return buttonClasses
    },
    computedVariant() {
      const defaultVariant = `${this.variant ? this.variant : 'primary'}`
      // const defaultVariant = 'primary'
      const variantMapping = {
        secondary: 'outline-primary',
        minimal: 'outline-primary',
        'secondary-red': 'outline-danger',
        success: 'success',
        warning: 'warning',
        info: 'info',
        'secondary-warning': 'outline-warning',
        'secondary-success': 'outline-success',
        'secondary-light': 'outline-primary',
        activated: 'activated',
        deactivated: 'deactivated',
        'icon-only': 'outline-primary',
      }

      return variantMapping[this.variant] || defaultVariant
    },
    computedClass() {
      const buttonClasses = []

      if (this.screenBreakpoint != null) {
        const shouldShowIconOnly =
          this.variant === 'icon-only' || (this.icon && !this.text) || this.isSmallScreen

        if (shouldShowIconOnly && this.icon !== null) {
          buttonClasses.push('mc-btn__icon')
        }
      } else {
        if (this.icon && !this.text) {
          buttonClasses.push('mc-btn__icon')
        }
      }

      return buttonClasses.join(' ')
    },
    buttonClass() {
      return [...this.baseButtonClasses, this.computedClass, this.block ? 'block-btn' : ''].join(' ').trim()
    },
    mainButtonClass() {
      return [...this.baseButtonClasses, this.computedClass, 'mc-button--split-main'].join(' ').trim()
    },
    actionButtonClass() {
      return [...this.baseButtonClasses, 'mc-btn__icon', 'mc-button--split-action'].join(' ').trim()
    },
    hasActionContent() {
      return Boolean(this.actionIcon || this.$slots['action-slot'])
    },
    isSplitButton() {
      return this.split && this.hasActionContent
    },
    isActionDisabled() {
      return this.disabled || this.loading || this.actionDisabled
    },
    shouldShowText() {
      const isIconOnlyVariant = this.variant === 'icon-only'
      if (this.screenBreakpoint != null) {
        const shouldHideTextForIcon = (isIconOnlyVariant || this.isSmallScreen) && this.icon != null

        return !shouldHideTextForIcon && this.text
      } else {
        return this.text && !isIconOnlyVariant
      }
    },
    shouldShowTooltip() {
      if (this.screenBreakpoint != null) {
        const isIconOnlyVariant = this.variant === 'icon-only'
        return (isIconOnlyVariant || (this.isSmallScreen && this.icon != null)) && this.text
      } else {
        return false
      }
    },
  },
  mounted() {
    if (this.screenBreakpoint != null) {
      this.checkScreenSize()
      window.addEventListener('resize', this.checkScreenSize)
    }
  },
  beforeDestroy() {
    if (this.screenBreakpoint != null) {
      window.removeEventListener('resize', this.checkScreenSize)
    }
  },
  methods: {
    checkScreenSize() {
      this.isSmallScreen = window.innerWidth < this.screenBreakpoint

      if (this.isSmallScreen) {
        this.tooltipVisible = false
      }
    },
    showTooltip() {
      if (this.isSmallScreen) {
        return
      }

      if (this.disabled || this.shouldShowTooltip || this.forceShowTooltip) {
        this.tooltipVisible = true
      }
    },
    hideTooltip() {
      this.tooltipVisible = false
    },
    handleActionClick(event) {
      if (this.isActionDisabled) {
        return
      }

      this.userLogging('ButtonClick', this.$route, `${this.text}-action`)
      this.$emit('action-click', event)
    },
    createLog() {
      this.userLogging('ButtonClick', this.$route, this.text)
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-button-wrapper {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

::v-deep .tooltip {
  position: absolute;
  background-color: $mep-color-text-400;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 550px !important;
  visibility: visible;
  opacity: 1;
  transition: 0.3s;
  min-width: 200px;

  white-space: normal !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;

  &.tooltip--nowrap {
    white-space: nowrap;
    word-break: break-word;
  }

  &[data-position='left'] {
    right: calc(100% + 5px);
    top: 50%;
    transform: translateY(-50%);
  }
  &[data-position='right'] {
    left: calc(100% + 5px);
    top: 50%;
    transform: translateY(-50%);
  }
  &[data-position='top'] {
    bottom: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
  }
  &[data-position='bottom'] {
    top: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
  }
}

.mc-button {
  display: flex;
  gap: 8px;
  padding: 8px 16px 8px 16px;

  &.block-btn {
    width: 100%;
    justify-content: center;
  }

  .mc-icon {
    vertical-align: bottom;
  }

  .mc-icon:before {
    font-size: 20px;
  }

  &.mc-btn__icon {
    padding: 8px;
  }
  &.mc-btn__small,
  &.mc-btn__sm {
    gap: 4px;
    padding: 4px 8px 4px 8px;
    border-radius: 3px;
    font-size: 11px;

    &.mc-btn__icon {
      padding: 4px;
    }

    .mc-icon:before {
      font-size: 16px;
    }

    .mc-icon {
      font-size: 16px;
    }
  }
  &.mc-btn__full {
    width: 100%;
    text-align: center; // Align text to the center
    display: flex; // Use flexbox for centering
    justify-content: center; // Center content horizontally
    align-items: center; // Center content vertically
  }

  &.mc-btn__primary {
    .mc-icon:before {
      color: $white;
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $gray;
      border-color: transparent;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }

    &:focus {
      outline: 2px solid $mep-primary-200 !important;
    }
  }

  &.mc-btn__secondary {
    .mc-icon:before {
      color: $mep-primary;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $mep-primary;

      .mc-icon:before {
        color: $white;
      }
    }

    &:focus {
      outline: 2px solid $mep-primary-200 !important;
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $white;
      border-color: $mep-color-text-200;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }

    &.mc-btn__table {
      display: block;
      margin: 7px auto;
      text-align: center;
    }
  }

  &.mc-btn__outline-primary {
    &:disabled {
      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__secondary-red {
    color: $red;
    background-color: $white;
    border-color: $red;
    .mc-icon:before {
      color: $red;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $red;
      border-color: $red;
      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $white;
      border-color: $mep-color-text-200;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__minimal {
    border-color: transparent;

    .mc-icon:before {
      color: $mep-primary;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $mep-primary;

      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: transparent;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__border-none {
    border: none !important;
  }

  &.mc-btn__success {
    color: $white;
    background-color: $green;
    border-color: $green;
    .mc-icon:before {
      color: $white;
    }
  }

  &.mc-btn__secondary-success {
    .mc-icon:before {
      color: $green;
    }

    &:hover,
    &:active,
    &:focus {
      .mc-icon:before {
        color: $white;
      }
    }

    &.disabled {
      color: $teal-300;
      border-color: $teal-300;

      .mc-icon:before {
        color: $teal-300;
      }
    }
  }

  &.mc-btn__info {
    background-color: $blue-100;
    color: rgba(28, 116, 218, 1);
    border-color: $blue-100;
    .mc-icon:before {
      color: rgba(28, 116, 218, 1);
    }

    &:hover,
    &:active,
    &:focus {
      opacity: 0.8;
    }
  }

  &.mc-btn__secondary-light {
    background-color: $white;
    .mc-icon:before {
      color: $mep-primary;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $mep-primary;

      .mc-icon:before {
        color: $white;
      }
    }

    &:focus {
      outline: 2px solid $mep-primary-200 !important;
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $white;
      border-color: $mep-color-text-200;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }

    &.mc-btn__table {
      display: block;
      margin: 7px auto;
      text-align: center;
    }
  }

  &.mc-btn__activated {
    color: $white;
    background-color: $green-400;
    border-color: $green-100;
    .mc-icon:before {
      color: $white;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white !important;
      background-color: $green-500 !important;
      border-color: $green-100 !important;
      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $teal-200;
      background-color: $gray;
      border-color: $teal-200;

      .mc-icon:before {
        color: $teal-200;
      }
    }
  }

  &.mc-btn__deactivated {
    color: $white;
    background-color: $teal-300;
    border-color: $teal-100;
    .mc-icon:before {
      color: $white;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white !important;
      background-color: $teal-400 !important;
      border-color: $teal-100 !important;
      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $teal-200;
      background-color: $gray;
      border-color: $teal-200;

      .mc-icon:before {
        color: $teal-200;
      }
    }
  }
}

.mc-button-split-group {
  display: inline-flex;
  width: auto;

  &.mc-button-split-group--block {
    display: flex;
    width: 100%;
  }

  .mc-button {
    margin: 0;
  }
}

.mc-button--split-main {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  flex: 1 1 auto;
  justify-content: center;
}

.mc-button--split-action {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.45);
  flex: 0 0 auto;
  min-width: 56px;
  justify-content: center;
  padding-left: 16px;
  padding-right: 16px;

  .mc-icon:before {
    font-size: 22px;
  }

  &.mc-btn__secondary,
  &.mc-btn__minimal,
  &.mc-btn__secondary-light {
    border-left-color: rgba($mep-primary, 0.2);
  }
}

.btn-download {
  .mc-button {
    width: 100% !important;
    justify-content: center !important;
  }
}
</style>
